import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { fetchWithAuth } from "../utils/api";
import { readPref, writePref } from "../utils/safeStorage";

// Which order page a person sees (orders overhaul Phase 4): the new one by
// default (Allen, Oct 19 2026: "on for everyone, can switch back") or the
// classic one. Saved with the person's other settings on the server
// (/user-settings/dashboard-config, key `order_page`), so it follows them to
// every device; this browser keeps a copy per person so the right page paints
// at once (a shared iPad never shows one person's choice to the next), and
// the server's answer wins when it arrives — unless they switched meanwhile.
// The profile menu and both pages switch it; every open order page hears the
// change.
const KEY = "order_page";
const LOCAL = "chariot-order-page";
const CHANGED = "chariot:order-page";
const pick = (value) => (value === "classic" ? "classic" : "new");

// One read per person per page load, shared by every order page opened in it.
let loaded = null; // { userId, promise }
const loadSaved = (userId) => {
  if (!loaded || loaded.userId !== userId) {
    const entry = { userId, promise: null };
    entry.promise = fetchWithAuth("/user-settings/dashboard-config")
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`settings ${res.status}`))))
      // Nothing saved there: this browser's copy stands.
      .then((body) => (body?.dashboard_config && KEY in body.dashboard_config ? pick(body.dashboard_config[KEY]) : null))
      // A failed read is tried again by the next order page opened.
      .catch(() => {
        if (loaded === entry) loaded = null;
        return null;
      });
    loaded = entry;
  }
  return loaded;
};

export default function useOrderPagePref() {
  const { user } = useAuth();
  const userId = user?.id ? String(user.id) : "";
  const local = `${LOCAL}:${userId}`;
  const [page, setPage] = useState(() => pick(readPref(local)));

  useEffect(() => {
    let live = true;
    setPage(pick(readPref(local)));
    const onChange = (e) => setPage(pick(e.detail));
    window.addEventListener(CHANGED, onChange);
    if (userId) {
      const entry = loadSaved(userId);
      entry.promise.then((saved) => {
        // A switch meanwhile replaced the read: that choice stands.
        if (!live || !saved || loaded !== entry) return;
        setPage(saved);
        writePref(local, saved);
      });
    }
    return () => {
      live = false;
      window.removeEventListener(CHANGED, onChange);
    };
  }, [userId, local]);

  const choose = useCallback((next) => {
    const value = pick(next);
    writePref(local, value);
    loaded = { userId, promise: Promise.resolve(value) };
    window.dispatchEvent(new CustomEvent(CHANGED, { detail: value }));
    // A background save: it stays out of the page's "Saved" check.
    fetchWithAuth("/user-settings/dashboard-config", {
      method: "PUT",
      body: JSON.stringify({ key: KEY, value }),
      skipSaveIndicator: true,
    }).catch(() => {});
  }, [userId, local]);

  return [page, choose];
}
